import {useCallback, useEffect, useRef, useSyncExternalStore} from 'react'
import {localStorageStore} from './local-storage-store'
import type {Listener} from './create-store'

const {
  cache,
  readStorage,
  writeStorage,
  resetStorage,
  subscribe,
  clearKeys,
  clearAll: clearAllLocalStorage,
} = localStorageStore

/**
 * Clears all store-tracked localStorage keys, their in-memory cache,
 * and the underlying browser localStorage.
 */
export function clearAllLocalStorageCache(): void {
  clearAllLocalStorage()
  if (typeof window !== 'undefined') {
    window.localStorage.clear()
  }
}

/**
 * Clears specified keys from localStorage and notifies subscribers
 */
export function clearLocalStorage(keys: string[]): void {
  clearKeys(keys)
}

/**
 * React hook for localStorage with cross-component synchronization.
 * Returns [value, setValue, reset] tuple.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  // Use ref for initialValue so it's available in callbacks without stale closure issues
  const initialValueRef = useRef(initialValue)
  useEffect(() => {
    initialValueRef.current = initialValue
  })

  // Seed lazily inside getSnapshot (idempotent) instead of during render
  // to avoid mutating external state in the render phase (concurrent-safe).
  // Uses initialValue directly (not ref) because getSnapshot runs during render
  // before the effect that updates initialValueRef.
  const getSnapshot = useCallback((): T => {
    if (!cache.has(key)) {
      readStorage(key, initialValue)
    }
    return cache.get(key) as T
  }, [key, initialValue])

  const getServerSnapshot = useCallback((): T => {
    return initialValue
  }, [initialValue])

  const subscribeToStore = useCallback((listener: Listener) => subscribe(key, listener), [key])

  const value = useSyncExternalStore(subscribeToStore, getSnapshot, getServerSnapshot)

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const current = cache.has(key) ? (cache.get(key) as T) : initialValueRef.current
      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(current) : next
      // Treat undefined as a reset to avoid writing "undefined" string to storage
      if (resolved === undefined) {
        resetStorage(key, initialValueRef.current)
      } else {
        writeStorage(key, resolved)
      }
    },
    [key],
  )

  const reset = useCallback(() => {
    resetStorage(key, initialValueRef.current)
  }, [key])

  return [value, setValue, reset] as const
}
